/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSubscription } from "store/auth/auth-store";
import { useState } from "react";
import {
  prefetchData,
  useSOWsAdmQuery,
} from "services/input-core/sows-adm/get-all-sows-adm";
import Card from "components/UI/Card/Card.js";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import CardBody from "components/UI/Card/CardBody.js";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import Skeleton from "react-loading-skeleton";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";
import { ColumnDef } from "@tanstack/react-table";
import { PaginatedResult } from "services/@types";
import { Sow } from "services/input-core/sows-adm/@types";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Tooltip } from "@material-ui/core";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import {
  getBadgeFromStatus,
  getFilters,
  getSorting,
  removeElementsFromArray,
} from "utils/functions";
import FieldglassButton from "components/UI/FieldglassButton";
import { Search } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { TitlePages } from "components/UI/TitlePage/TitlePages";
import { useGetSowBlacklist } from "services/input-core/appconfigs/get-blacklisted-entities";
import { FilterSowStatusEnum } from "utils/@types";
import { useSowAdmContext } from "store/sowADM";

export default function List() {
  const { subscription } = useSubscription();
  const queryClient = useQueryClient();
  const { t } = useTranslation("sow-table");

  const columns: ColumnDef<any, any>[] = [
    {
      header: "Status",
      footer: (props: any) => props.column.id,
      accessorKey: "status",
      cell: (info: any) =>
        getBadgeFromStatus(info.getValue(), (text: string) =>
          t(text, { ns: "status" })
        ),
    },
    {
      footer: (props: any) => props.column.id,
      header: "ID",
      accessorKey: "sowID",
    },
    {
      header: t("name"),
      footer: (props: any) => props.column.id,
      accessorKey: "sowName",
    },
    {
      header: t("supplier"),
      footer: (props: any) => props.column.id,
      accessorKey: "supplierName",
    },
    {
      header: t("start"),
      accessorKey: "startDate",
      cell: (info: any) => info.getValue(),
      sortDescFirst: true,
    },
    {
      header: t("end"),
      accessorKey: "endDate",
      cell: (info: any) => info.getValue(),
      sortDescFirst: true,
    },
    {
      header: t("actions"),
      accessorKey: "actions",
      cell: ({ row }: any) => (
        <div className="actions-right">
          <Tooltip placement="top" title="View">
            <Link to={"/app/sowsAdm/profile?id=" + row?.original?.sowID}>
              <ButtonTS
                round
                justIcon
                size="sm"
                color="info"
                key={new Date().getTime()}
              >
                <ArrowForwardIosIcon />
              </ButtonTS>
            </Link>
          </Tooltip>
        </div>
      ),
      enableColumnFilter: false,
    },
  ];

  const [tableData, setTableData] = useState<any>({
    pageSize: 10,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const { getSows, sows } = useSowAdmContext();


  const { data, isLoading, isFetching } = useSOWsAdmQuery(
    {
      "page[size]": tableData.pageSize,
      "page[index]": tableData.pageIndex,
      ...getFilters(tableData.columnFilters),
      ...getSorting(tableData.sorting),
    },
    {
      staleTime: 1000 * 60 * 5,
      onSuccess: (data: PaginatedResult<Sow>) => {
        prefetchData(
          queryClient,
          {
            "page[size]": tableData.pageSize,
            "page[index]": tableData.pageIndex + 1,
            ...getFilters(tableData.columnFilters),
            ...getSorting(tableData.sorting),
          },
          subscription?.subscriptionId
        );
        prefetchData(
          queryClient,
          {
            "page[size]": tableData.pageSize,
            "page[index]":
              tableData.pageIndex > 0 ? tableData.pageIndex - 1 : 0,
            ...getFilters(tableData.columnFilters),
            ...getSorting(tableData.sorting),
          },
          subscription?.subscriptionId
        );
        setTableData((state: any) => ({
          ...state,
          pageSize: data.perPage,
          pageIndex: data.toSkip / data.perPage,
        }));
        getSows(data.result);
      },
    }
  );

  const { data: sowAdmBlacklist } = useGetSowBlacklist();

  return (
    <Card>
      <CardHeader color="rose" icon>
        <CardIcon color="rose">
          <Search />
        </CardIcon>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <TitlePages type="sidebar" title="sows-admin" />
          <FieldglassButton type="sow" />
        </div>
      </CardHeader>
      <CardBody>
        {isLoading || isFetching ? (
          <Skeleton />
        ) : (
          <PaginatedTable
            id="sows-adm-table"
            columns={columns}
            data={
              removeElementsFromArray(
                data?.result,
                sowAdmBlacklist?.sowBlackList ?? []
              ) || []
            }
            tableState={tableData}
            pageCount={data?.totalPages}
            setTableState={setTableData}
            fixedPageSize
            statusToFilter={FilterSowStatusEnum}
          />
        )}
      </CardBody>
    </Card>
  );
}
